// src/components/documents/DocumentLineItems.js — doced-8f3a61c2 (Document editor, Stage 4)
//
// The line items section of the full-page editor. ESTIMATES ONLY for now (the invoice, job and
// recurring pages get their own rules when they're built).
//
// Compact rows: drag handle (or arrow keys on it) · item + description · qty · unit · price · tax ·
// amount · "⋯". The less-used settings live behind "⋯": Service / Material, what the customer can do
// with the line (Required / Recommended / Add-on / Included in another line), "Save to catalog" (Owner /
// Admin / Manager only), "Unlink from catalog", move, delete. Every edit is the pop-up's
// (estimateEditor.setLineField), and a catalog-linked line's name can't be typed over, as in the pop-up.
//
// Amounts come from the totals engine (per line, to the cent), the same figures the totals panel adds.
//
// doced-8f3a61c2 — Stage 5h: below 640px wide (phones) each line is a CARD instead of a table row — name,
// description, then Qty / Unit / Price, then Tax and the amount — with every control at least 44px and no
// sideways scroll. Reordering on a card is "Move up / Move down" in "⋯" (no drag handle). Each line's fields
// are built ONCE (lineFields) and only one layout is drawn, so field ids stay unique. Without matchMedia
// (tests) the table is drawn, as before.
//
// taxpick4-54573069 — Stage S4 (tax codes on every line): `taxPick` is given ONLY when the company's
// Settings › Taxes switch is on (estimateLineTax.js). Without it (every real company today) the Tax
// column is exactly the pre-S4 dropdown. With it: one dropdown per line from the company's codes (or its
// QuickBooks codes), a stopped code shown with an amber border and its note, "No tax" on light amber;
// people who don't pick tax see the line's tax as text; office roles see "Added in the field — check tax".

import React, { useEffect, useRef, useState } from 'react';
import { GripVertical, MoreHorizontal, Link2, Trash2, ArrowUp, ArrowDown, Unlink, Save } from 'lucide-react';
import { SELECTION_TYPES } from '../../utils/quoteSchema';
import {
    setLineField, lineNumberValue, QTY_MAX, PRICE_MAX, moveLine, parentChoices,
    taxChoicesFor, lineTaxValue, applyTaxChoice, EXEMPT_CHOICE,
} from '../../utils/estimateEditor';
import { unlinkEstimateLine } from '../../utils/catalogPickLines';
import { formatCurrency } from '../../utils/formatters';
import { SELECT, SELECT_STYLE, LINE_FIELD, LINE_SELECT } from './fieldStyles';
import LineDescriptionInput from './LineDescriptionInput'; // linetext-4d8b2e61 — every document page's line description box
import { linePicker, applyLinePick, fieldTaxCheckOf, FIELD_TAX_CHECK_TEXT } from '../../utils/estimateLineTax'; // taxpick4-54573069

const SELECTION_WORDS = {
    [SELECTION_TYPES.REQUIRED]: { label: 'Required', help: 'Always included' },
    [SELECTION_TYPES.RECOMMENDED]: { label: 'Recommended', help: 'Ticked; the customer can untick it' },
    [SELECTION_TYPES.ADDON]: { label: 'Add-on', help: 'Unticked; the customer can add it' },
    [SELECTION_TYPES.INCLUDED]: { label: 'Included in another line', help: 'Shown under its parent, at no extra charge' },
};

const lineName = (l, i) => (l.name || '').trim() || `line ${i + 1}`;

// doced-8f3a61c2 — Stage 5h: true below the `sm` breakpoint (640px), following the window as it resizes.
const NARROW_QUERY = '(max-width: 639px)';
function useNarrow() {
    const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(NARROW_QUERY).matches);
    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return undefined;
        const mq = window.matchMedia(NARROW_QUERY);
        const on = () => setNarrow(mq.matches);
        on();
        if (mq.addEventListener) { mq.addEventListener('change', on); return () => mq.removeEventListener('change', on); }
        mq.addListener(on); return () => mq.removeListener(on);
    }, []);
    return narrow;
}

// The menu opens in a fixed position beside its button, so the table's sideways scroll can't clip it.
// It closes on a click elsewhere, Escape, or the page scrolling/resizing (it would drift off its button).
function LineMenu({ line, index, lines, onChange, onMove, onDelete, canSaveToCatalog, onClose, anchor }) {
    const ref = useRef(null);
    const closeRef = useRef(onClose);
    closeRef.current = onClose;
    const [pos, setPos] = useState(null);
    useEffect(() => {
        const r = anchor && anchor.getBoundingClientRect();
        if (r) {
            const width = 288;
            const left = Math.max(8, Math.min(r.right - width, window.innerWidth - width - 8));
            const below = window.innerHeight - r.bottom;
            setPos(below > 360 ? { left, top: r.bottom + 4 } : { left, bottom: window.innerHeight - r.top + 4 });
        }
        const away = (e) => { if (ref.current && !ref.current.contains(e.target) && !(anchor && anchor.contains(e.target))) closeRef.current(); };
        const key = (e) => { if (e.key === 'Escape') { closeRef.current(); if (anchor) anchor.focus(); } };
        const drift = (e) => { if (ref.current && e && e.target && ref.current.contains(e.target)) return; closeRef.current(); };
        document.addEventListener('mousedown', away);
        document.addEventListener('keydown', key);
        window.addEventListener('scroll', drift, true);
        window.addEventListener('resize', drift);
        const t = setTimeout(() => { const first = ref.current && ref.current.querySelector('select, input, button'); if (first) first.focus(); }, 0);
        return () => {
            clearTimeout(t);
            document.removeEventListener('mousedown', away);
            document.removeEventListener('keydown', key);
            window.removeEventListener('scroll', drift, true);
            window.removeEventListener('resize', drift);
        };
    }, [anchor]);
    const set = (name, value) => onChange(setLineField(line, name, value));
    const parents = parentChoices(lines, index);
    const btn = 'flex w-full items-center gap-2 min-h-[44px] px-3 rounded-lg text-sm text-left hover:bg-gray-50 disabled:opacity-40 disabled:hover:bg-transparent';
    return (
        <div ref={ref} role="dialog" aria-label={`More options for ${lineName(line, index)}`} style={pos || { visibility: 'hidden' }}
            className="fixed z-[65] w-72 max-h-[80vh] overflow-y-auto bg-white border border-gray-200 rounded-xl shadow-xl p-3 flex flex-col gap-3 text-left">
            <div className="flex flex-col gap-1">
                <label htmlFor={`doced-type-${index}`} className="text-xs font-semibold text-gray-600">Type</label>
                <select id={`doced-type-${index}`} value={line.type || 'Service'} onChange={(e) => set('type', e.target.value)}
                    style={SELECT_STYLE} className={`${SELECT} !text-sm`}>
                    <option value="Service">Service</option>
                    <option value="Material">Material</option>
                </select>
            </div>
            <div className="flex flex-col gap-1">
                <label htmlFor={`doced-sel-${index}`} className="text-xs font-semibold text-gray-600">What the customer can do</label>
                <select id={`doced-sel-${index}`} value={line.selectionType || SELECTION_TYPES.REQUIRED} onChange={(e) => set('selectionType', e.target.value)}
                    style={SELECT_STYLE} className={`${SELECT} !text-sm`}>
                    {[SELECTION_TYPES.REQUIRED, SELECTION_TYPES.RECOMMENDED, SELECTION_TYPES.ADDON, SELECTION_TYPES.INCLUDED].map((t) => (
                        <option key={t} value={t}>{SELECTION_WORDS[t].label}</option>
                    ))}
                </select>
                <span className="text-xs text-gray-500">{(SELECTION_WORDS[line.selectionType] || SELECTION_WORDS.required).help}{line.selectionType === SELECTION_TYPES.INCLUDED ? '. Its price is set to $0.' : ''}</span>
            </div>
            {line.selectionType === SELECTION_TYPES.INCLUDED && (
                <div className="flex flex-col gap-1">
                    <label htmlFor={`doced-parent-${index}`} className="text-xs font-semibold text-gray-600">Included in</label>
                    <select id={`doced-parent-${index}`} value={line.parentItemId || ''} onChange={(e) => set('parentItemId', e.target.value || null)}
                        style={SELECT_STYLE} className={`${SELECT} !text-sm ${line.parentItemId ? '' : '!border-red-400'}`}>
                        <option value="">Choose a line…</option>
                        {parents.map((p) => <option key={p.itemId} value={p.itemId}>{(p.name || '').trim() || '(unnamed line)'}</option>)}
                    </select>
                    {parents.length === 0 && <span className="text-xs text-red-600">Add another line first — an included line needs a line to belong to.</span>}
                </div>
            )}
            <div className="border-t border-gray-100 pt-2 flex flex-col">
                {canSaveToCatalog && !line.serviceId && (line.name || '').trim() && (
                    <label className={`${btn} cursor-pointer`}>
                        <input type="checkbox" checked={!!line.addToCatalog} onChange={(e) => set('addToCatalog', e.target.checked)} className="w-5 h-5 accent-[#5F4EC9]" />
                        <Save size={16} className="text-gray-500" aria-hidden="true" />
                        <span>Save to the catalog when I save</span>
                    </label>
                )}
                {line.serviceId && (
                    <button type="button" className={btn} onClick={() => { onChange(unlinkEstimateLine(line)); onClose(); }}>
                        <Unlink size={16} className="text-gray-500" aria-hidden="true" />Unlink from catalog (keep the words and price)
                    </button>
                )}
                <button type="button" className={btn} disabled={index === 0} onClick={() => { onMove(index, index - 1); onClose(); }}>
                    <ArrowUp size={16} className="text-gray-500" aria-hidden="true" />Move up
                </button>
                <button type="button" className={btn} disabled={index === lines.length - 1} onClick={() => { onMove(index, index + 1); onClose(); }}>
                    <ArrowDown size={16} className="text-gray-500" aria-hidden="true" />Move down
                </button>
                <button type="button" className={`${btn} text-red-700 hover:bg-red-50`} onClick={() => { onDelete(index); onClose(); }}>
                    <Trash2 size={16} aria-hidden="true" />Delete this line
                </button>
            </div>
        </div>
    );
}

export default function DocumentLineItems({ lines, onLinesChange, totalsLines, taxCtx, currency, canSaveToCatalog, showErrors, footer, taxPick }) {
    const [menuFor, setMenuFor] = useState(null); // { key, anchor } of the open "⋯"
    const [armed, setArmed] = useState(null); // index whose handle is held (enables dragging that row)
    const [dragFrom, setDragFrom] = useState(null);
    const [dragOver, setDragOver] = useState(null);
    const [announce, setAnnounce] = useState('');
    const narrow = useNarrow(); // doced-8f3a61c2 — Stage 5h

    const amountAt = (i) => {
        const t = (totalsLines || []).find((l) => l.index === i);
        return t ? t.amount : 0;
    };
    const update = (i, line) => onLinesChange(lines.map((l, j) => (j === i ? line : l)));
    const move = (from, to) => {
        if (to < 0 || to >= lines.length) return;
        onLinesChange(moveLine(lines, from, to));
        setAnnounce(`${lineName(lines[from], from)} moved to position ${to + 1} of ${lines.length}.`);
    };
    const remove = (i) => {
        onLinesChange(lines.filter((_, j) => j !== i));
        setAnnounce(`${lineName(lines[i], i)} deleted.`);
    };
    const keyOf = (l, i) => l.lineId || l.itemId || `i${i}`;
    const cell = 'px-2 py-2 align-top';
    const input = LINE_FIELD;

    // One line's fields, built once and laid out as a table row (wide) or a card (narrow).
    const lineFields = (line, i) => {
        const name = lineName(line, i);
        const k = keyOf(line, i);
        const nameMissing = showErrors && !(line.name || '').trim();
        const choices = taxPick ? null : taxChoicesFor(line, taxCtx);
        const taxValue = taxPick ? null : lineTaxValue(line);
        const pick = taxPick ? linePicker(line, taxPick.ctx, taxCtx) : null; // taxpick4-54573069
        const parent = line.selectionType === SELECTION_TYPES.INCLUDED ? lines.find((p) => p.itemId === line.parentItemId) : null;
        return {
            name, k,
            nameField: (
                <div className="relative">
                    <label htmlFor={`doced-name-${k}`} className="sr-only">Item name, line {i + 1}</label>
                    <input id={`doced-name-${k}`} value={line.name || ''} disabled={!!line.serviceId}
                        onChange={(e) => update(i, setLineField(line, 'name', e.target.value))}
                        placeholder="Item name (required)"
                        className={`${input} font-semibold ${line.serviceId ? 'pl-7' : ''} ${nameMissing ? '!border-red-400' : ''}`} />
                    {line.serviceId && <Link2 size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#5F4EC9]" aria-label="From the catalog" />}
                </div>
            ),
            descField: (
                <LineDescriptionInput id={`doced-desc-${k}`} value={line.description || ''} look="page"
                    onChange={(e) => update(i, setLineField(line, 'description', e.target.value))}
                    labelClassName="block mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500" />
            ),
            chip: line.selectionType && line.selectionType !== SELECTION_TYPES.REQUIRED ? (
                <button type="button" onClick={(e) => setMenuFor({ key: k, anchor: e.currentTarget })}
                    className={`self-start ${narrow ? 'min-h-[44px] px-3' : 'min-h-[28px] px-2'} rounded-full text-xs font-semibold ${line.selectionType === SELECTION_TYPES.INCLUDED && !parent ? 'bg-red-50 text-red-700' : 'bg-gray-100 text-gray-700'}`}>
                    {line.selectionType === SELECTION_TYPES.INCLUDED
                        ? (parent ? `Included in: ${lineName(parent, lines.indexOf(parent))}` : 'Included — choose which line it belongs to')
                        : SELECTION_WORDS[line.selectionType]?.label || line.selectionType}
                </button>
            ) : null,
            catalogNote: line.addToCatalog && !line.serviceId ? <span className="text-xs text-[#4f40b0] font-semibold">Will be saved to the catalog</span> : null,
            // taxpick4-54573069 — a technician's line, for office roles until an office role saves.
            fieldNote: taxPick && taxPick.showFieldMarker && fieldTaxCheckOf(line)
                ? <span className="self-start px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-xs font-semibold text-amber-800">{FIELD_TAX_CHECK_TEXT}</span>
                : null,
            qtyField: (
                <>
                    <label htmlFor={`doced-qty-${k}`} className="sr-only">Quantity for {name}</label>
                    <input id={`doced-qty-${k}`} type="number" inputMode="decimal" min="0" max={QTY_MAX} step="0.01" value={line.quantity}
                        onChange={(e) => { const v = lineNumberValue(e.target.value, QTY_MAX); if (v !== undefined) update(i, setLineField(line, 'quantity', v)); }}
                        className={`${input} text-center tabular-nums`} />
                </>
            ),
            unitField: (
                <>
                    <label htmlFor={`doced-unit-${k}`} className="sr-only">Unit for {name}</label>
                    <input id={`doced-unit-${k}`} value={line.unit || ''} maxLength={20} placeholder="—"
                        onChange={(e) => update(i, setLineField(line, 'unit', e.target.value))}
                        className={input} />
                </>
            ),
            priceField: (
                <>
                    <label htmlFor={`doced-price-${k}`} className="sr-only">Price for {name}</label>
                    <input id={`doced-price-${k}`} type="number" inputMode="decimal" min="0" max={PRICE_MAX} step="0.01" value={line.price}
                        onChange={(e) => { const v = lineNumberValue(e.target.value, PRICE_MAX); if (v !== undefined) update(i, setLineField(line, 'price', v)); }}
                        className={`${input} text-right tabular-nums`} />
                </>
            ),
            taxField: pick ? (
                <>
                    {taxPick.canPick ? (
                        <>
                            <label htmlFor={`doced-tax-${k}`} className="sr-only">Tax for {name}</label>
                            <select id={`doced-tax-${k}`} value={pick.value} onChange={(e) => update(i, applyLinePick(line, e.target.value, taxPick.ctx))}
                                title={(pick.choices.find((c) => c.value === pick.value) || {}).title}
                                style={SELECT_STYLE} className={`${LINE_SELECT} ${pick.exempt ? '!bg-amber-50' : ''} ${pick.stopped ? '!border-amber-400 !border-2' : ''}`}>
                                {pick.choices.map((c) => <option key={c.value || 'default'} value={c.value} title={c.title}>{c.label}</option>)}
                            </select>
                        </>
                    ) : (
                        <span className="block min-h-[44px] leading-[44px] text-sm text-gray-700 truncate">{(pick.choices.find((c) => c.value === pick.value) || {}).label || ''}</span>
                    )}
                    {pick.note && <span className="block mt-1 text-xs text-amber-700">{pick.note}</span>}
                </>
            ) : (
                <>
                    <label htmlFor={`doced-tax-${k}`} className="sr-only">Tax for {name}</label>
                    <select id={`doced-tax-${k}`} value={taxValue} onChange={(e) => update(i, applyTaxChoice(line, e.target.value))}
                        title={(choices.find((c) => c.value === taxValue) || {}).title}
                        style={SELECT_STYLE} className={`${LINE_SELECT} ${taxValue === EXEMPT_CHOICE ? '!bg-amber-50' : ''}`}>
                        {choices.map((c) => <option key={c.value || 'default'} value={c.value} title={c.title}>{c.label}</option>)}
                    </select>
                </>
            ),
            amount: formatCurrency(amountAt(i), currency),
            more: (
                <>
                    <button type="button" aria-label={`More options for ${name}`} aria-haspopup="dialog" aria-expanded={menuFor?.key === k}
                        onClick={(e) => setMenuFor(menuFor?.key === k ? null : { key: k, anchor: e.currentTarget })}
                        className="w-11 h-11 inline-flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100">
                        <MoreHorizontal size={18} aria-hidden="true" />
                    </button>
                    {menuFor?.key === k && (
                        <LineMenu line={line} index={i} lines={lines} onChange={(l) => update(i, l)} onMove={move} onDelete={remove}
                            canSaveToCatalog={canSaveToCatalog} onClose={() => setMenuFor(null)} anchor={menuFor.anchor} />
                    )}
                </>
            ),
        };
    };

    // doced-8f3a61c2 — Stage 5h: phones — one card per line, nothing wider than the screen.
    if (narrow) {
        const cap = 'block mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500';
        return (
            <div className="flex flex-col gap-3">
                <div className="sr-only" aria-live="polite">{announce}</div>
                {lines.map((line, i) => {
                    const f = lineFields(line, i);
                    return (
                        <div key={f.k} role="group" aria-label={`Line ${i + 1}: ${f.name}`} className="border border-gray-200 rounded-xl p-3 flex flex-col gap-2 min-w-0">
                            <div className="flex items-start gap-2">
                                <div className="flex-1 min-w-0">{f.nameField}</div>
                                <div className="shrink-0">{f.more}</div>
                            </div>
                            {f.descField}
                            <div className="grid grid-cols-3 gap-2">
                                <div className="min-w-0"><span className={cap} aria-hidden="true">Qty</span>{f.qtyField}</div>
                                <div className="min-w-0"><span className={cap} aria-hidden="true">Unit</span>{f.unitField}</div>
                                <div className="min-w-0"><span className={cap} aria-hidden="true">Price</span>{f.priceField}</div>
                            </div>
                            <div className="flex items-end gap-3">
                                <div className="flex-1 min-w-0"><span className={cap} aria-hidden="true">Tax</span>{f.taxField}</div>
                                <div className="shrink-0 text-right">
                                    <span className={cap} aria-hidden="true">Amount</span>
                                    <span className="block min-h-[44px] leading-[44px] font-bold tabular-nums">{f.amount}</span>
                                </div>
                            </div>
                            {f.chip}
                            {f.catalogNote}
                            {f.fieldNote}
                        </div>
                    );
                })}
                {lines.length === 0 && <p className="py-4 text-sm text-gray-500">No lines yet. Type below to add one, or add from the catalog.</p>}
                <div className="min-w-0">{footer}</div>
            </div>
        );
    }

    return (
        <div>
            <div className="sr-only" aria-live="polite">{announce}</div>
            <div className="overflow-x-auto">
                <table className="w-full min-w-[780px] border-collapse text-sm">
                    <thead>
                        <tr className="text-left text-gray-500 text-xs uppercase tracking-wide border-b border-gray-100">
                            <th className="w-8"><span className="sr-only">Reorder</span></th>
                            <th className="px-2 py-2 font-semibold">Item</th>
                            <th className="px-2 py-2 font-semibold w-[84px]">Qty</th>
                            <th className="px-2 py-2 font-semibold w-[84px]">Unit</th>
                            <th className="px-2 py-2 font-semibold w-[110px] text-right">Price</th>
                            <th className="px-2 py-2 font-semibold w-[168px]">Tax</th>
                            <th className="px-2 py-2 font-semibold w-[100px] text-right">Amount</th>
                            <th className="w-12"><span className="sr-only">More</span></th>
                        </tr>
                    </thead>
                    <tbody>
                        {lines.map((line, i) => {
                            const f = lineFields(line, i);
                            const { name, k } = f;
                            return (
                                <tr key={k}
                                    draggable={armed === i}
                                    onDragStart={(e) => { setDragFrom(i); e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', String(i)); } catch (_) { /* some browsers */ } }}
                                    onDragOver={(e) => { if (dragFrom !== null) { e.preventDefault(); setDragOver(i); } }}
                                    onDrop={(e) => { e.preventDefault(); if (dragFrom !== null) move(dragFrom, i); setDragFrom(null); setDragOver(null); setArmed(null); }}
                                    onDragEnd={() => { setDragFrom(null); setDragOver(null); setArmed(null); }}
                                    className={`border-b border-gray-100 ${dragOver === i && dragFrom !== null && dragFrom !== i ? 'bg-[#5F4EC9]/5' : ''} ${dragFrom === i ? 'opacity-50' : ''}`}>
                                    <td className="pt-3 pl-1 align-top">
                                        <button type="button"
                                            aria-label={`Drag to reorder ${name}. Or use the up and down arrow keys.`}
                                            onPointerDown={() => setArmed(i)} onPointerUp={() => { if (dragFrom === null) setArmed(null); }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'ArrowUp') { e.preventDefault(); move(i, i - 1); }
                                                if (e.key === 'ArrowDown') { e.preventDefault(); move(i, i + 1); }
                                            }}
                                            className="w-7 h-10 inline-flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-grab rounded">
                                            <GripVertical size={16} aria-hidden="true" />
                                        </button>
                                    </td>
                                    <td className={cell}>
                                        <div className="flex flex-col gap-1">
                                            {f.nameField}
                                            {f.descField}
                                            {f.chip}
                                            {f.catalogNote}
                                            {f.fieldNote}
                                        </div>
                                    </td>
                                    <td className={cell}>{f.qtyField}</td>
                                    <td className={cell}>{f.unitField}</td>
                                    <td className={cell}>{f.priceField}</td>
                                    <td className={cell}>{f.taxField}</td>
                                    <td className={`${cell} pt-4 text-right font-bold tabular-nums`}>{f.amount}</td>
                                    <td className="pt-2 align-top">{f.more}</td>
                                </tr>
                            );
                        })}
                        {lines.length === 0 && (
                            <tr><td /><td colSpan={7} className="px-2 py-6 text-sm text-gray-500">No lines yet. Type below to add one, or add from the catalog.</td></tr>
                        )}
                        <tr>
                            <td />
                            <td colSpan={7} className="px-2 py-3">{footer}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
}
